import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { colorScheme, widgetAppearanceType, widgetComponentType, widgetType } from 'src/app/enums';
import { gridItem, property, propertyExt } from 'src/app/models';
import { DashboardService } from 'src/app/services/services/dashboard.service';
import { widgetAppearance } from '../../models';
import { ColorPickerComponent } from '../color-picker/color-picker.component';

@Component({
    selector: 'app-preferences',
    templateUrl: './preferences.component.html',
    styleUrls: ['./preferences.component.scss'],
    standalone: false
})
export class PreferencesComponent implements OnInit, OnChanges {
  @Input() public gridItem: gridItem;
  @Input() public colorScheme: colorScheme;

  public fontSizes = [8, 9, 10, 11, 12, 14, 16, 17, 18, 20, 24, 30, 40, 48, 72, 100, 200, 240].map(m => ({ name: m + "px", value: m }));
  public appearance: widgetAppearance[] = [];
  propertyIds: number[] = [];
  properties: propertyExt[] = [];
  enableOrder: boolean = false;


  constructor(private DashboardsService: DashboardService, private i18nextPipe: I18NextPipe, public dialog: MatDialog) { }

  ngOnInit(): void {
    this.init();
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes.colorScheme) {
      this.init();
    }
  }

  drop(event: CdkDragDrop<propertyExt[]>) {
    if (event.previousIndex == event.currentIndex) return;
    moveItemInArray(this.properties, event.previousIndex, event.currentIndex);
    moveItemInArray(this.propertyIds, event.previousIndex, event.currentIndex);
  }

  init() {
    let setup = this.gridItem.setup;
    this.enableOrder = false;
    if (!setup.preferences) setup.preferences = {};
    if (!setup.preferences.names) setup.preferences.names = [];
    if (!setup.preferences.fonts) setup.preferences.fonts = [];
    if (!setup.preferences.colors) setup.preferences.colors = [];


    if (this.gridItem.type == widgetType.Label && (this.gridItem.setup.queueIds?.length > 1 || this.gridItem.setup.agentIds?.length > 1)) {
      this.enableOrder = true;
      this.propertyIds = this.gridItem.componentType == widgetComponentType.Agents ? setup.agentIds : setup.queueIds;
      let list$ = this.gridItem.componentType == widgetComponentType.Agents ? this.DashboardsService.getAllAgents() : this.DashboardsService.getAllQueues();
      list$.pipe(take(1)).subscribe(props => {
        if (this.gridItem.type == widgetType.Table || this.gridItem.type == widgetType.Label) {
          this.enableOrder = true;
          this.properties = this.propertyIds.map(id => {
            let name = setup?.preferences?.names?.find(p => p.id == id)?.name;
            if (this.gridItem.componentType == widgetComponentType.Agents) {
              let agentName = props.find(p => p.userId == id)?.name;
              return { id, name: name || agentName, propName: agentName };
            } else {
              let queueName = props.find(p => p.id == id)?.description;
              return { id, name: name || queueName, propName: queueName };
            }
          });
        }
        this.setAppearance(null);
      });
    } else {
      let propName: string;
      let properties$:Observable<property[]>
      switch (this.gridItem.componentType) {
        case widgetComponentType.Agents:
          propName = "agentPropertiesIds";
          properties$=this.DashboardsService.getAgentProperties();
          break;
        case widgetComponentType.Calls:
          propName = "traficIds";
           properties$=this.DashboardsService.getQueueProperties();
          break;
        case widgetComponentType.Queues:
          propName = "queuePropertiesIds";
          properties$=this.DashboardsService.getQueueProperties();
          break;
        case widgetComponentType.Reports:
        case widgetComponentType.RuleMatch:
          propName = "columns";
          properties$=this.DashboardsService.getReportColumns(setup.reportId,setup.reportSubId);
          break;
        default:
          break;
      }

      this.propertyIds = setup[propName];
      properties$.pipe(take(1)).subscribe(props => {
        if (this.gridItem.type == widgetType.Table || this.gridItem.type == widgetType.Label) {
          this.enableOrder = true;
          this.properties = this.propertyIds.map(id => {
            let propName = props.find(p => p.id == id)?.name;
            if (propName) { propName = this.i18nextPipe.transform('dashboard:' + propName); }
            let name = setup?.preferences?.names?.find(p => p.id == id)?.name;
            return { id, name: propName, propName };
          });
        }
        this.setAppearance(props);
      });
    }
  }

  setAppearance(props) {
    let isDark = this.gridItem.colorScheme == colorScheme.Dark;
    let isAsh = this.gridItem.colorScheme == colorScheme.Ash;
    let isDefault = this.gridItem.colorScheme == colorScheme.Default;

    switch (this.gridItem.type) {
      case widgetType.Table: {
        this.appearance = [
          { type: widgetAppearanceType.Titles, label: "Header", editName: false, defFontSize: 14, defColor: (isDefault || isAsh) ? "#000000" : "#FFFFFF" },
          { type: widgetAppearanceType.Values, label: "Row", editName: false, defColor: isDark ? "#FFFFFF" : "#000000", defFontSize: 14 }
        ]
        break;
      }
      case widgetType.Label: {
        this.appearance = [
          { type: widgetAppearanceType.Titles, label: "Label Title", editName: false, defColor: isAsh ? "#000000" : "#FFFFFF", defFontSize: 17 },
          { type: widgetAppearanceType.Values, label: "Label Values", editName: false, defColor: isAsh ? "#000000" : "#FFFFFF", defFontSize: 40 }
        ]
        break;
      }
      default: {
        let propName = props.find(p => p.id == this.propertyIds[0])?.name;
        if (propName) propName = this.i18nextPipe.transform('dashboard:' + propName);
        let isTraffic = this.gridItem.componentType == widgetComponentType.Calls;
        let defColor = isDark ?  "#FFFFFF":"#30424F" ;
        this.appearance = [
          { type: widgetAppearanceType.LeggendTitle, label: "Legend Title", editName: true, defColor, defFontSize: 12 },
          { type: widgetAppearanceType.LeggendElements, label: "Legend elements", editName: false, defColor, defFontSize: 12 },
          { type: widgetAppearanceType.XAxisTitle, label: "X Axis Title", editName: true, defColor, defFontSize: 11 },
          { type: widgetAppearanceType.XAxisLables, label: "X Axis Lables", editName: false, defColor, defFontSize: 12 },
          { type: widgetAppearanceType.YAxisTitle, label: "Y Axis Title", editName: true, defColor, defFontSize: 11 },
          { type: widgetAppearanceType.YAxisLables, label: "Y Axis Labels", editName: false, defColor, defFontSize: 12 },
        ]
        if (this.gridItem.componentType != widgetComponentType.Calls && this.gridItem.componentType != widgetComponentType.Reports) {
          if (this.gridItem.type == widgetType.Barchart) {
            this.appearance.find(a => a.type == widgetAppearanceType.YAxisTitle).defName = propName;
          }
        }
        break;
      }
    }
    this.appearance.forEach(el => {
      if (el.editName) {
        let recordName = this.gridItem.setup.preferences.names.find(p => p.id == el.type);
        el.name = recordName?.name != null ? recordName.name : el.defName;
      }
      let font = this.gridItem.setup.preferences.fonts.find(a => a.id == el.type);
      el.fontSize = font?.fontSize != null ? font.fontSize : el.defFontSize;
      let color = this.gridItem.setup.preferences.colors.find(a => a.id == el.type);
      el.fontSize = font?.fontSize != null ? font.fontSize : el.defFontSize;
      el.color = color?.color != null ? color.color : el.defColor;
    });
  }

  onNameChanged(prop: propertyExt) {
    let setup = this.gridItem.setup;
    if (prop.name != prop.propName) {
      let propertyName = setup.preferences.names.find(p => p.id == prop.id);
      if (propertyName != null) {
        propertyName.name = prop.name;
      } else {
        setup.preferences.names.push({ id: prop.id, name: prop.name });
      }
    } else {
      setup.preferences.names = setup.preferences.names.filter(p => p.id != prop.id);
    }
  }

  onFontSizeChanged($event, a: widgetAppearance) {
    let pref = this.gridItem.setup.preferences;
    pref.fonts = pref.fonts.filter(p => p.id != a.type);
    if (a.fontSize != a.defFontSize) {
      pref.fonts.push({ id: a.type, fontSize: a.fontSize });
    }
  }

  openColorPicker($event, a: widgetAppearance) {
    let position = $event.currentTarget.getBoundingClientRect();
    const dialogRef = this.dialog.open(ColorPickerComponent, {
      width: '200px',
      data: { color: a.color },
      backdropClass: "backdrop-invisible",
      panelClass: "color-picker",
      position: { top: `${position.top - 305}px`, left: `${position.left - 185}px` },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!result?.color) return;
      let newColor = result.color;
      a.color = newColor;
      let pref = this.gridItem.setup.preferences;
      pref.colors = pref.colors.filter(p => p.id != a.type);
      if (newColor.toLowerCase() != a.defColor.toLowerCase()) {
        pref.colors.push({ id: a.type, color: newColor });
      }
    });
  }
}
